/**
 * Vencimentos: o que está vencido ou a vencer, de todas as fontes, numa tela só.
 *
 * Hoje as empresas controlam isso em planilha, e a informação está espalhada por cinco telas —
 * exame na Convocação, EPI em EPIs, treinamento em Treinamentos, calibração na Home. Esta tela
 * só ganha da planilha se fizer as duas coisas que a planilha não faz: nunca estar desatualizada
 * (lê do mesmo dado que emite o ASO) e levar à ação — por isso a linha é clicável e vai para a
 * tela onde se resolve aquele vencimento.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, MenuItem, Stack, TextField, useTheme } from '@mui/material';
import {
  Error as ErrorIcon,
  Event as EventIcon,
  FileDownload as DownloadIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { PageShell, DataTable, StatCard, type DataTableColumn } from '../../components/common';
import { vencimentoService } from '../../services/vencimentoService';
import {
  HORIZONTES,
  type Horizonte,
  type ItemDeVencimento,
  type PainelDeVencimentos,
} from '../../types/vencimento';

/** Onde se resolve cada tipo de vencimento. Sem isto a tela vira relatório que ninguém usa. */
const ONDE_RESOLVER: Record<string, string> = {
  exam: '/exam-summons',
  epi: '/epis',
  epc: '/epcs',
  training: '/trainings',
  certificate: '/certificates',
  'measurement-equipment': '/measurement-equipments',
  cnh: '/employees',
  ecac: '/companies',
};

const TODOS = '__todos__';

function dataBonita(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** "venceu há 5 dias" diz mais que "-5", e é como a pessoa fala do problema. */
function prazoEmPalavras(dias: number): string {
  if (dias < 0) return `venceu há ${String(Math.abs(dias))} ${Math.abs(dias) === 1 ? 'dia' : 'dias'}`;
  if (dias === 0) return 'vence hoje';
  if (dias === 1) return 'vence amanhã';
  return `em ${String(dias)} dias`;
}

const colunas: DataTableColumn<ItemDeVencimento>[] = [
  {
    id: 'situacao',
    label: 'Situação',
    render: (i) =>
      i.situacao === 'vencido' ? (
        <Chip size="small" label="Vencido" color="error" />
      ) : i.situacao === 'vence_em_breve' ? (
        <Chip size="small" label="Vence em breve" color="warning" />
      ) : (
        <Chip size="small" label="No prazo" color="default" />
      ),
  },
  { id: 'item', label: 'Item', primary: true, render: (i) => i.item },
  { id: 'rotulo', label: 'Tipo', render: (i) => i.rotulo },
  { id: 'empresa', label: 'Empresa', render: (i) => i.empresa ?? '—' },
  { id: 'vencimentoEm', label: 'Vencimento', render: (i) => dataBonita(i.vencimentoEm) },
  { id: 'diasRestantes', label: 'Prazo', render: (i) => prazoEmPalavras(i.diasRestantes) },
];

export const VencimentosPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { palette: cores } = useTheme();

  const [painel, setPainel] = useState<PainelDeVencimentos | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [horizonte, setHorizonte] = useState<Horizonte>(30);
  const [tipo, setTipo] = useState<string>(TODOS);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const resposta = await vencimentoService.painel({ dias: horizonte });
      setPainel(resposta);
    } catch (erro: unknown) {
      enqueueSnackbar(
        erro instanceof Error ? erro.message : 'Não foi possível carregar os vencimentos',
        { variant: 'error' }
      );
      setPainel(null);
    } finally {
      setCarregando(false);
    }
  }, [horizonte, enqueueSnackbar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // O filtro de tipo é local: o painel já veio inteiro, e ir ao servidor de novo só para
  // esconder linhas piscaria a tela sem trazer nada novo.
  const itens = useMemo(() => {
    if (!painel) return [];
    return tipo === TODOS ? painel.itens : painel.itens.filter((i) => i.tipo === tipo);
  }, [painel, tipo]);

  const exportar = useCallback(() => {
    if (itens.length === 0) return;
    const cabecalho = ['Situação', 'Item', 'Tipo', 'Empresa', 'Vencimento', 'Prazo'];
    const linhas = itens.map((i) => [
      i.situacao === 'vencido' ? 'Vencido' : i.situacao === 'vence_em_breve' ? 'Vence em breve' : 'No prazo',
      i.item,
      i.rotulo,
      i.empresa ?? '',
      dataBonita(i.vencimentoEm),
      prazoEmPalavras(i.diasRestantes),
    ]);
    // Ponto e vírgula e BOM: é assim que o Excel em português abre o arquivo sem pedir nada.
    const csv = [cabecalho, ...linhas]
      .map((linha) => linha.map((c) => `"${c.replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vencimentos-${String(horizonte)}-dias.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    enqueueSnackbar(`${String(itens.length)} vencimento(s) exportado(s)`, { variant: 'success' });
  }, [itens, horizonte, enqueueSnackbar]);

  const tiposDisponiveis = painel?.porTipo ?? [];
  const filtrandoPorTipo = tipo !== TODOS;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Vencimentos' }]}
      title="Vencimentos"
      subtitle="O que já venceu e o que está para vencer, de exames a EPIs"
      actions={
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportar}
          disabled={itens.length === 0}
        >
          Exportar
        </Button>
      }
    >
      {/*
        Grade de colunas iguais, e não `Stack`: em linha flex cada cartão encolhe até o próprio
        texto, e os três saíam com larguras diferentes só porque um rótulo é mais comprido que o
        outro. Aqui os três ocupam a mesma fração, e a altura empareja porque o StatCard já é
        `height: 100%` — o cartão sem linha de apoio não fica mais baixo que os outros.
      */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard
          icon={<ErrorIcon />}
          label="Vencidos"
          value={String(painel?.vencidos ?? 0)}
          sublabel="prazo já passou"
          color={cores.error.main}
        />
        <StatCard
          icon={<WarningIcon />}
          label="Vencem em breve"
          value={String(painel?.venceEmBreve ?? 0)}
          sublabel="nos próximos 10 dias"
          color={cores.warning.main}
        />
        <StatCard
          icon={<EventIcon />}
          label="No período"
          value={String(painel?.total ?? 0)}
          sublabel={`próximos ${String(horizonte)} dias`}
        />
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Horizonte"
          value={horizonte}
          onChange={(e) => setHorizonte(Number(e.target.value) as Horizonte)}
          sx={{ minWidth: 200 }}
        >
          {HORIZONTES.map((h) => (
            <MenuItem key={h} value={h}>
              Próximos {h} dias
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Tipo"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value={TODOS}>Todos os tipos</MenuItem>
          {tiposDisponiveis.map((t) => (
            <MenuItem key={t.chave} value={t.chave}>
              {t.rotulo} ({t.total})
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box>
        <DataTable
          columns={colunas}
          rows={itens}
          getRowId={(i) => `${i.tipo}:${i.item}:${i.vencimentoEm}`}
          loading={carregando}
          onRowClick={(i) => {
            const destino = ONDE_RESOLVER[i.tipo];
            if (destino) navigate(destino);
          }}
          filtersActive={filtrandoPorTipo}
          onClearFilters={() => setTipo(TODOS)}
          emptyState={{
            title: 'Nada vencendo neste período, no seu acesso',
            description:
              'Aumente o horizonte para 60 ou 90 dias para enxergar mais longe. Exames, EPIs, treinamentos e calibrações aparecem aqui conforme a data de validade se aproxima.',
          }}
        />
      </Box>
    </PageShell>
  );
};

export default VencimentosPage;
